'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './audit.css';

/* All styling lives in audit.css (scoped to .audit-page).
   Header/Footer come from the site layout, not this file.
   Images used from public/assets/images/ (existing files, swap for audit-*.jpg later):
   asset-hero.jpg (hero full background),
   manufacturing2.jpg (assurance image), asset-ai.jpg (impact), asset-transform.jpg (story),
   Utilities-&-Energy.jpg (approach banner), asset-cta.jpg (final CTA)
   Links: `areas` aur `industries` arrays me `href` field me badlo. Jis item ka page nahi hai uska href nahi hai. */

type IconName =
  | 'chart' | 'search' | 'shield' | 'bulb' | 'db' | 'alert' | 'gear' | 'doc'
  | 'clock' | 'trend' | 'users' | 'monitor' | 'pulse' | 'layers' | 'target'
  | 'cloud' | 'building' | 'leaf' | 'factory' | 'cart' | 'bank' | 'bolt' | 'heart';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    chart: <><path d="M4 20V4M4 20h16" /><rect x="8" y="12" width="3" height="6" /><rect x="13" y="8" width="3" height="10" /></>,
    search: <><circle cx="11" cy="11" r="6" /><path d="m20 20-4.2-4.2" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    bulb: <><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></>,
    db: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3 3 7 3s7-1.3 7-3v-6" /></>,
    alert: <><path d="M12 3 2.5 20h19z" /><path d="M12 10v4M12 17h.01" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    trend: <><path d="M4 17 9 11l4 3 7-8" /><path d="M15 6h5v5" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    monitor: <><rect x="3" y="4" width="18" height="12" rx="1" /><path d="M8 20h8M12 16v4" /></>,
    pulse: <><path d="M3 12h4l2-6 4 12 2-6h6" /></>,
    layers: <><path d="m12 3 9 5-9 5-9-5z" /><path d="m3 13 9 5 9-5" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>,
    cloud: <><path d="M7 18a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8.5 4.8 4.8 0 0 1 17 18z" /></>,
    building: <><path d="M5 21V4h9v17M14 9h5v12M3 21h18M8 8h3M8 12h3M8 16h3" /></>,
    leaf: <><path d="M5 19c0-8 5-14 15-14 0 10-6 15-14 15" /><path d="M5 19c3-4 6-6 10-8" /></>,
    factory: <><path d="M3 21V10l6 4v-4l6 4V6h6v15z" /><path d="M7 17h2M13 17h2M18 17h1" /></>,
    cart: <><circle cx="9" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" /><path d="M3 4h3l2.5 11h10L21 7H7" /></>,
    bank: <><path d="M3 9 12 4l9 5H3zM5 9v9M9.5 9v9M14.5 9v9M19 9v9M3 21h18" /></>,
    bolt: <><path d="M13 3 5 13h6l-1 8 8-10h-6z" /></>,
    heart: <><path d="M12 20s-8-4.6-8-10.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 8 2.5C20 15.4 12 20 12 20z" /></>,
  };
  return (
    <svg className="au-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

/* href = card ka link (header ke menu se). Jin areas ka alag page nahi hai unme href nahi hai. */
const areas: { icon: IconName; title: string; text: string; href?: string }[] = [
  { icon: 'db', title: 'Financial audit', text: 'Accurate, data-backed audits of financial statements and controls.' },
  { icon: 'alert', title: 'Risk assurance', text: 'Early visibility into risk across processes, vendors and systems.' },
  { icon: 'shield', title: 'Compliance & regulatory', text: 'Stay aligned with regulations and internal policies.', href: '/Operate/controls-compliance' },
  { icon: 'gear', title: 'Process & internal audit', text: 'Test process efficiency and strengthen internal controls.' },
];

const outcomes: { icon: IconName; value: string; label: string }[] = [
  { icon: 'clock', value: '40%', label: 'Faster audit cycle time' },
  { icon: 'trend', value: '60%', label: 'Better risk detection and prevention' },
  { icon: 'users', value: 'Higher', label: 'Stakeholder trust and confidence' },
];

const capabilities: { icon: IconName; title: string }[] = [
  { icon: 'monitor', title: 'Real-time monitoring' },
  { icon: 'pulse', title: 'AI-driven anomaly detection' },
  { icon: 'shield', title: 'Continuous compliance' },
  { icon: 'chart', title: 'Deeper business insights' },
];

const approach: { icon: IconName; title: string; text: string }[] = [
  { icon: 'doc', title: 'Understand', text: 'Assess business context, risks and regulatory requirements.' },
  { icon: 'chart', title: 'Analyse', text: 'Leverage data and AI to identify exceptions and insights.' },
  { icon: 'shield', title: 'Assure', text: 'Validate findings and strengthen controls and processes.' },
  { icon: 'bulb', title: 'Enable', text: 'Provide actionable recommendations for continuous improvement.' },
];

const storyStats: { value: string; label: string }[] = [
  { value: '70%', label: 'Reduction in manual effort' },
  { value: '50%', label: 'Higher risk detection accuracy' },
  { value: '100%', label: 'Improved compliance adherence' },
];

const otherCases: { icon: IconName; label: string }[] = [
  { icon: 'layers', label: 'Internal audit transformation' },
  { icon: 'doc', label: 'Regulatory compliance support' },
  { icon: 'cloud', label: 'Financial statement audit' },
  { icon: 'db', label: 'Third-party risk assessment' },
  { icon: 'leaf', label: 'ESG assurance' },
];

const industries: { icon: IconName; name: string; href: string }[] = [
  { icon: 'factory', name: 'Manufacturing', href: '/industries/manufacturing' },
  { icon: 'cart', name: 'Retail & Consumer', href: '/industries/fmcg-retail' },
  { icon: 'bank', name: 'Banking & Financial Services', href: '/industries/bfsi' },
  { icon: 'bolt', name: 'Energy & Utilities', href: '/industries/energy-utilities' },
  { icon: 'heart', name: 'Life Sciences & Healthcare', href: '/industries/pharma-lifescience' },
];

/* ===================== SCROLL REVEAL ===================== */
/* One fade-up on section content. Content stays visible if JS or IntersectionObserver is missing.
   The "js" class is added first so .rv only hides content once the observer is running. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.classList.add('js');
    root.querySelectorAll<HTMLElement>('.sec-head, .area, .out, .cap-item, .ap-step, .case-row, .ind').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 60}ms`);
      io.observe(n);
    });
    return () => {
      io.disconnect();
      root.classList.remove('js');
    };
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function Audit() {
  const ref = useReveal();

  return (
    <div className="audit-page" ref={ref}>
      <main>
        {/* HERO: text on the left, image on the right */}
        <section className="au-hero">
          <div className="au-hero-bg"></div>
          <div className="au-hero-overlay"></div>
          <div className="op-container au-hero-inner">
            <div className="au-hero-content">
              <h1 className="au-hero-title">Smarter audit. Stronger trust.</h1>
              <p className="au-hero-text">
                We combine data, technology and domain expertise to deliver intelligent audit and
                assurance solutions that enhance transparency, mitigate risk and build stakeholder confidence.
              </p>
              <div className="au-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Audit Experts</Link>
                <Link className="btn-outline" href="#use-cases">Explore Use Cases</Link>
              </div>
            </div>
          </div>
        </section>

        {/* ASSURANCE: quadrant + outcome strip */}
        <section className="op-section">
          <div className="op-container">
            <div className="assure-grid">
              <div className="assure-left">
                <div className="sec-head assure-copy">
                  <h2 className="sec-title">Assurance for a more transparent tomorrow.</h2>
                  <p className="sec-sub">
                    Our audit and assurance solutions help organisations strengthen governance, improve
                    process efficiency and gain reliable, data-driven insights for confident decision-making.
                  </p>
                </div>
                <div className="assure-photo">
                  <img
                    src="/assets/images/manufacturing2.jpg"
                    alt="Audit team reviewing business processes"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="quad">
                {areas.map((a) => {
                  const body = (
                    <>
                      <Icon name={a.icon} />
                      <h3 className="card-title">{a.title}</h3>
                      <p className="card-text">{a.text}</p>
                    </>
                  );
                  /* href na ho to card plain article rahega (click nahi hoga) */
                  return a.href ? (
                    <Link className="area" key={a.title} href={a.href}>
                      {body}
                    </Link>
                  ) : (
                    <article className="area" key={a.title}>
                      {body}
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="out-strip">
              <h3 className="out-title">Business outcomes</h3>
              <div className="out-grid">
                {outcomes.map((o) => (
                  <div className="out" key={o.label}>
                    <Icon name={o.icon} />
                    <div>
                      <strong>{o.value}</strong>
                      <small>{o.label}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* DATA TO IMPACT */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="impact">
              <div className="impact-copy">
                <div className="sec-head">
                  <h2 className="sec-title">From data analysis to real impact.</h2>
                  <p className="sec-sub">
                    Our AI-enabled audit solutions analyse large datasets, detect anomalies and provide
                    actionable insights, so you can move from reactive audits to proactive assurance.
                  </p>
                </div>
                <ul className="cap-list">
                  {capabilities.map((c) => (
                    <li className="cap-item" key={c.title}>
                      <span className="cap-icon"><Icon name={c.icon} /></span>
                      <span>{c.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="impact-photo" role="img" aria-label="AI-driven audit dashboard with insights"></div>
            </div>
          </div>
        </section>

        {/* APPROACH: banner image + 4 steps */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Our approach</h2>
              <p className="sec-sub">Four steps from understanding your risks to continuous improvement.</p>
            </div>
            <div className="ap-banner">
              <img
                src="/assets/images/Utilities-%26-Energy.jpg"
                alt="Enterprise operations reviewed through audit and assurance"
                loading="lazy"
              />
            </div>
            <ol className="ap-grid">
              {approach.map((a, i) => (
                <li className="ap-step" key={a.title}>
                  <div className="ap-top">
                    <span className="ap-num">{String(i + 1).padStart(2, '0')}</span>
                    <Icon name={a.icon} />
                  </div>
                  <h3 className="card-title">{a.title}</h3>
                  <p className="card-text">{a.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* CUSTOMER STORY + OTHER USE CASES */}
        <section className="op-section op-section--ruled" id="use-cases">
          <div className="op-container">
            <div className="story-grid">
              <div className="story">
                <div className="story-photo" role="img" aria-label="Business transformation through audit analytics"></div>
                <div className="story-copy">
                  <p className="story-label">Customer story</p>
                  <h2 className="sec-title">Enhanced audit efficiency for a global enterprise.</h2>
                  <p className="sec-sub">
                    Orane helped a global enterprise automate data analysis for internal audits, reducing
                    manual effort by 70% and improving risk detection accuracy by 50%.
                  </p>
                </div>
                <div className="story-stats">
                  {storyStats.map((s) => (
                    <div className="story-stat" key={s.label}>
                      <strong>{s.value}</strong>
                      <small>{s.label}</small>
                    </div>
                  ))}
                </div>
              </div>

              <aside className="cases">
                <h3 className="cases-title">Other use cases</h3>
                <ul>
                  {otherCases.map((c) => (
                    <li className="case-row" key={c.label}>
                      <Icon name={c.icon} />
                      <span>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Industries we serve</h2>
            </div>
            <div className="ind-row">
              {industries.map((i) => (
                <Link className="ind" key={i.name} href={i.href}>
                  <Icon name={i.icon} />
                  <span>{i.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="au-cta" id="contact">
          <div className="au-cta-bg"></div>
          <div className="au-cta-overlay"></div>
          <div className="op-container au-cta-inner">
            <h2 className="au-cta-title">Let&rsquo;s build a more transparent future.</h2>
            <p className="au-cta-text">
              Tell us where you need stronger assurance. We&rsquo;ll help you plan the audit approach
              and deliver insights you can act on.
            </p>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Experts</Link>
          </div>
        </section>
      </main>
    </div>
  );
}